import React, { useEffect, useMemo, useState } from 'react'
import { Checkbox, Form, Input } from 'semantic-ui-react'
import { AppDropdown as Dropdown } from 'components/AppDropdown'
import { AppButton, AppPrimaryButton } from 'elements'
import { GetReportTimeZoneList } from 'hooks'
import {
    buildLivePreview,
    CADENCE_MONTHLY,
    CADENCE_WEEKLY,
    emptyFormState,
    HOUR_OPTIONS,
    SEND_MINUTE_OPTIONS,
    scheduleToForm,
    toIanaTimezone,
    validateExternalEmails,
    WEEK_STARTS_ON,
    WINDOW_KIND,
    WINDOW_KIND_META,
    WINDOW_KIND_OPTIONS,
} from './consolidated-report-email-utils'
import {
    DayCell,
    DayPicker,
    FooterActions,
    FormGrid,
    MonthCell,
    MonthGrid,
    NewBadge,
    PreviewCard,
    PreviewColumn,
    RecentFiresSection,
    ToggleCard,
    WeekStartPill,
    WindowCard,
    WindowSubRow,
} from './consolidated-report-email-styles'
import { RunHistoryList } from './RunHistoryList'
import { EmailContentSection } from './EmailContentSection'
import { keepChosenTemplateFields } from './email-content-utils'
import { EmailPreviewSection } from './EmailPreviewSection'

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const remainingOptions = (options, selected) =>
    (options || []).filter(o => !(selected || []).includes(o.value))

export const ScheduleForm = ({
    initialSchedule,
    isEdit,
    loading,
    fieldErrors = {},
    bannerError,
    onHoldBanner,
    timezone,
    templateOptions = [],
    templateRecords = [],
    userOptions = [],
    auditorOptions = [],
    onSubmit,
    onCancel,
    onTestSend,
    onFireNow,
    onDelete,
    recentRuns = [],
    runsLoading,
    onRunClick,
}) => {
    const [form, setForm] = useState(() =>
        emptyFormState(toIanaTimezone(timezone)),
    )
    const [emailValidationMsg, setEmailValidationMsg] = useState('')
    const [auditorsOpen, setAuditorsOpen] = useState(false)
    const [usersOpen, setUsersOpen] = useState(false)
    const { options: timezoneOptions, loading: timezonesLoading } =
        GetReportTimeZoneList()

    useEffect(() => {
        setForm(scheduleToForm(initialSchedule, timezone))
    }, [initialSchedule, timezone])

    const effectiveTimezone = form.timeZone || toIanaTimezone(timezone)

    const preview = useMemo(
        () => buildLivePreview(form, effectiveTimezone),
        [form, effectiveTimezone],
    )

    const effectiveAuditorOptions = auditorOptions.length
        ? auditorOptions
        : userOptions

    const auditorsLeft = remainingOptions(
        effectiveAuditorOptions,
        form.auditorUserIds,
    )
    const usersLeft = remainingOptions(userOptions, form.userIds)

    const setField = (key, value) => {
        setForm(prev => ({ ...prev, [key]: value }))
    }

    // Several fields at once (template list + columns change together).
    const setFields = patch => {
        setForm(prev => ({ ...prev, ...patch }))
    }

    const toggleWeekDay = day => {
        setForm(prev => {
            const days = prev.weekDays.includes(day)
                ? prev.weekDays.filter(d => d !== day)
                : [...prev.weekDays, day].sort((a, b) => a - b)
            return { ...prev, weekDays: days.length ? days : [day] }
        })
    }

    const toggleMonthDay = day => {
        setForm(prev => {
            const days = prev.monthDays.includes(day)
                ? prev.monthDays.filter(d => d !== day)
                : [...prev.monthDays, day].sort((a, b) => a - b)
            return { ...prev, monthDays: days.length ? days : [day] }
        })
    }

    const handleExternalBlur = () => {
        const { invalid } = validateExternalEmails(form.externalEmails)
        if (invalid.length) {
            setEmailValidationMsg(
                `Invalid email${invalid.length > 1 ? 's' : ''}: ${invalid.join(', ')}`,
            )
        } else {
            setEmailValidationMsg('')
        }
    }

    const handleSubmit = e => {
        e.preventDefault()
        // The server removes the fields of templates taken off the schedule and refuses to save
        // them, so they are dropped here (the screen lists them as "removed when you save").
        onSubmit(
            form.includeAllTemplates
                ? form
                : {
                      ...form,
                      columns: keepChosenTemplateFields(
                          form.columns,
                          form.templateIds,
                          templateRecords,
                      ),
                  },
        )
    }

    return (
        <Form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
                <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>
                    {isEdit
                        ? `Edit schedule — ${initialSchedule?.ScheduleName || ''}`
                        : 'New Consolidated Report Schedule'}
                </h3>
                <div style={{ color: '#6b7280', fontSize: 12 }}>
                    Fill in what to include, when to send, and who receives it.
                    Live preview on the right.
                </div>
            </div>

            {bannerError && <div className="banner-error">{bannerError}</div>}
            {onHoldBanner && (
                <div className="banner-warn">{onHoldBanner}</div>
            )}

            <FormGrid>
                <div>
                    <Form.Field required error={Boolean(fieldErrors.ScheduleName)}>
                        <label>Schedule name</label>
                        <Input
                            placeholder="e.g. Exec Weekly Digest"
                            value={form.scheduleName}
                            onChange={(_, { value }) =>
                                setField('scheduleName', value)
                            }
                        />
                        {fieldErrors.ScheduleName && (
                            <div className="field-error">
                                {fieldErrors.ScheduleName}
                            </div>
                        )}
                    </Form.Field>

                    <ToggleCard>
                        <Checkbox
                            toggle
                            checked={form.isActive}
                            onChange={(_, { checked }) =>
                                setField('isActive', checked)
                            }
                        />
                        <div>
                            <strong>Active</strong>
                            <small>Uncheck to disable this schedule entirely.</small>
                        </div>
                    </ToggleCard>
                    <ToggleCard>
                        <Checkbox
                            toggle
                            checked={form.onHold}
                            onChange={(_, { checked }) =>
                                setField('onHold', checked)
                            }
                        />
                        <div>
                            <strong>Hold sending</strong>
                            <small>
                                Pauses recurring send without deleting config.
                            </small>
                        </div>
                    </ToggleCard>
                    <ToggleCard>
                        <Checkbox
                            toggle
                            checked={form.sendEmptyDigest}
                            onChange={(_, { checked }) =>
                                setField('sendEmptyDigest', checked)
                            }
                        />
                        <div>
                            <strong>Send empty digest</strong>
                            <small>
                                OFF = skip send when window has 0 reports.
                            </small>
                        </div>
                    </ToggleCard>

                    <Form.Field>
                        <label>
                            Include reports from <NewBadge>NEW</NewBadge>
                        </label>
                        <div
                            style={{
                                color: '#6b7280',
                                fontSize: 12,
                                marginBottom: 8,
                                lineHeight: 1.45,
                            }}
                        >
                            Which audits go into each digest. &quot;Since last
                            email&quot; gives a rolling stream with no gaps or
                            overlap. &quot;This week so far&quot; and the other
                            named windows are useful when the boss says{' '}
                            <em>
                                &quot;I want everything from this Monday through
                                today&quot;
                            </em>
                            .
                        </div>
                        <Dropdown
                            fluid
                            selection
                            options={WINDOW_KIND_OPTIONS}
                            value={form.windowKind || WINDOW_KIND.SinceLastFire}
                            onChange={(_, { value }) =>
                                setField('windowKind', value)
                            }
                        />
                        <WindowCard>
                            <div className="kind-label">
                                {
                                    (
                                        WINDOW_KIND_META[form.windowKind] ||
                                        WINDOW_KIND_META[
                                            WINDOW_KIND.SinceLastFire
                                        ]
                                    ).label
                                }
                            </div>
                            <div className="kind-help">
                                {
                                    (
                                        WINDOW_KIND_META[form.windowKind] ||
                                        WINDOW_KIND_META[
                                            WINDOW_KIND.SinceLastFire
                                        ]
                                    ).help
                                }
                            </div>
                            {(form.windowKind === WINDOW_KIND.CurrentWeek ||
                                form.windowKind ===
                                    WINDOW_KIND.PreviousWeek) && (
                                <WindowSubRow>
                                    <span>Week starts on:</span>
                                    <WeekStartPill
                                        type="button"
                                        $active={
                                            form.weekStartsOn ===
                                            WEEK_STARTS_ON.Monday
                                        }
                                        onClick={() =>
                                            setField(
                                                'weekStartsOn',
                                                WEEK_STARTS_ON.Monday,
                                            )
                                        }
                                    >
                                        Monday
                                    </WeekStartPill>
                                    <WeekStartPill
                                        type="button"
                                        $active={
                                            form.weekStartsOn ===
                                            WEEK_STARTS_ON.Sunday
                                        }
                                        onClick={() =>
                                            setField(
                                                'weekStartsOn',
                                                WEEK_STARTS_ON.Sunday,
                                            )
                                        }
                                    >
                                        Sunday
                                    </WeekStartPill>
                                </WindowSubRow>
                            )}
                            {form.windowKind === WINDOW_KIND.RollingDays && (
                                <WindowSubRow>
                                    <span>Last</span>
                                    <Input
                                        type="number"
                                        min={1}
                                        max={365}
                                        style={{ width: 90 }}
                                        value={form.rollingDays}
                                        onChange={(_, { value }) =>
                                            setField('rollingDays', value)
                                        }
                                    />
                                    <span>days</span>
                                </WindowSubRow>
                            )}
                        </WindowCard>
                    </Form.Field>

                    <Form.Field error={Boolean(fieldErrors.AuditorUserIds)}>
                        <label>Auditors (completed by)</label>
                        <div className="d-flex flex-wrap align-items-center" style={{ gap: 16 }}>
                            <Checkbox
                                label="All auditors"
                                checked={form.includeAllAuditors}
                                onChange={(_, { checked }) => {
                                    setField('includeAllAuditors', checked)
                                    if (checked) setField('auditorUserIds', [])
                                }}
                            />
                            <Checkbox
                                label="All statuses"
                                checked={Boolean(form.sendAnyStatus)}
                                onChange={(_, { checked }) =>
                                    setField('sendAnyStatus', checked)
                                }
                            />
                        </div>
                        {!form.includeAllAuditors && (
                            <Dropdown
                                fluid
                                multiple
                                search
                                selection
                                options={effectiveAuditorOptions}
                                value={form.auditorUserIds}
                                open={auditorsOpen}
                                onOpen={() => setAuditorsOpen(true)}
                                onClose={() => setAuditorsOpen(false)}
                                onChange={(_, { value }) => {
                                    setField('auditorUserIds', value)
                                    if (
                                        remainingOptions(
                                            effectiveAuditorOptions,
                                            value,
                                        ).length === 0
                                    ) {
                                        setAuditorsOpen(false)
                                    }
                                }}
                                noResultsMessage={
                                    auditorsLeft.length === 0
                                        ? 'No more auditors available'
                                        : 'No results found'
                                }
                                placeholder="Add auditor..."
                                className="mt-2"
                            />
                        )}
                        {fieldErrors.AuditorUserIds && (
                            <div className="field-error">
                                {fieldErrors.AuditorUserIds}
                            </div>
                        )}
                    </Form.Field>

                    <EmailContentSection
                        includeAllTemplates={form.includeAllTemplates}
                        templateIds={form.templateIds}
                        templateOrderSaved={Boolean(form.templateOrderSaved)}
                        columns={form.columns || []}
                        onChange={setFields}
                        templateOptions={templateOptions}
                        templateRecords={templateRecords}
                        templateError={fieldErrors.TemplateIds}
                    />

                    <Form.Field>
                        <label>Cadence</label>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <AppButton
                                type="button"
                                primary={form.cadence === CADENCE_WEEKLY}
                                onClick={() => setField('cadence', CADENCE_WEEKLY)}
                            >
                                Weekly
                            </AppButton>
                            <AppButton
                                type="button"
                                primary={form.cadence === CADENCE_MONTHLY}
                                onClick={() =>
                                    setField('cadence', CADENCE_MONTHLY)
                                }
                            >
                                Monthly
                            </AppButton>
                        </div>
                    </Form.Field>

                    {form.cadence === CADENCE_WEEKLY ? (
                        <Form.Field error={Boolean(fieldErrors.WeekDays)}>
                            <label>Days of week (pick at least 1)</label>
                            <DayPicker>
                                {DAY_NAMES.map((name, idx) => (
                                    <DayCell
                                        key={name}
                                        type="button"
                                        $selected={form.weekDays.includes(idx + 1)}
                                        onClick={() => toggleWeekDay(idx + 1)}
                                    >
                                        {name}
                                    </DayCell>
                                ))}
                            </DayPicker>
                            <div
                                style={{
                                    marginTop: 8,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    fontSize: 12,
                                }}
                            >
                                Repeat every
                                <Input
                                    type="number"
                                    min={1}
                                    max={52}
                                    style={{ width: 60 }}
                                    value={form.repeatInterval}
                                    onChange={(_, { value }) =>
                                        setField('repeatInterval', value)
                                    }
                                />
                                week(s)
                            </div>
                            {fieldErrors.WeekDays && (
                                <div className="field-error">
                                    {fieldErrors.WeekDays}
                                </div>
                            )}
                            {fieldErrors.RepeatInterval && (
                                <div className="field-error">
                                    {fieldErrors.RepeatInterval}
                                </div>
                            )}
                        </Form.Field>
                    ) : (
                        <Form.Field error={Boolean(fieldErrors.MonthDays)}>
                            <label>Days of month (pick at least 1)</label>
                            <MonthGrid>
                                {Array.from({ length: 31 }, (_, i) => i + 1).map(
                                    day => (
                                        <MonthCell
                                            key={day}
                                            type="button"
                                            $selected={form.monthDays.includes(day)}
                                            onClick={() => toggleMonthDay(day)}
                                        >
                                            {day}
                                        </MonthCell>
                                    ),
                                )}
                                <MonthCell
                                    type="button"
                                    $selected={form.monthDays.includes(32)}
                                    onClick={() => toggleMonthDay(32)}
                                >
                                    Last
                                </MonthCell>
                            </MonthGrid>
                            <div
                                style={{
                                    marginTop: 8,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    fontSize: 12,
                                }}
                            >
                                Repeat every
                                <Input
                                    type="number"
                                    min={1}
                                    max={12}
                                    style={{ width: 60 }}
                                    value={form.repeatInterval}
                                    onChange={(_, { value }) =>
                                        setField('repeatInterval', value)
                                    }
                                />
                                month(s)
                            </div>
                            {fieldErrors.MonthDays && (
                                <div className="field-error">
                                    {fieldErrors.MonthDays}
                                </div>
                            )}
                        </Form.Field>
                    )}

                    <Form.Field>
                        <label>Send time (in company TZ)</label>
                        <div
                            style={{
                                display: 'flex',
                                gap: 6,
                                alignItems: 'center',
                                flexWrap: 'nowrap',
                                width: '100%',
                            }}
                        >
                            <Dropdown
                                selection
                                compact
                                options={HOUR_OPTIONS}
                                value={form.sendHour}
                                onChange={(_, { value }) =>
                                    setField('sendHour', value)
                                }
                                style={{ minWidth: 72, flex: '0 0 auto' }}
                            />
                            <span>:</span>
                            <Dropdown
                                selection
                                compact
                                options={SEND_MINUTE_OPTIONS}
                                value={form.sendMinute}
                                onChange={(_, { value }) =>
                                    setField('sendMinute', value)
                                }
                                style={{ minWidth: 72, flex: '0 0 auto' }}
                            />
                            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                                <Dropdown
                                    selection
                                    search
                                    fluid
                                    loading={timezonesLoading}
                                    options={timezoneOptions}
                                    value={form.timeZone}
                                    onChange={(_, { value }) =>
                                        setField('timeZone', value)
                                    }
                                    placeholder="Select timezone..."
                                />
                            </div>
                        </div>
                    </Form.Field>

                    <Form.Field error={Boolean(fieldErrors.Recipients)}>
                        <label>Recipients — internal users</label>
                        <Dropdown
                            fluid
                            multiple
                            search
                            selection
                            options={userOptions}
                            value={form.userIds}
                            open={usersOpen}
                            onOpen={() => setUsersOpen(true)}
                            onClose={() => setUsersOpen(false)}
                            onChange={(_, { value }) => {
                                setField('userIds', value)
                                if (
                                    remainingOptions(userOptions, value)
                                        .length === 0
                                ) {
                                    setUsersOpen(false)
                                }
                            }}
                            noResultsMessage={
                                usersLeft.length === 0
                                    ? 'No more users available'
                                    : 'No results found'
                            }
                            placeholder="Add user..."
                        />
                    </Form.Field>

                    <Form.Field error={Boolean(fieldErrors.Recipients)}>
                        <label>Recipients — external emails</label>
                        <Form.TextArea
                            rows={2}
                            placeholder="board@company.com; ops@partner.com"
                            value={form.externalEmails}
                            onChange={(_, { value }) =>
                                setField('externalEmails', value)
                            }
                            onBlur={handleExternalBlur}
                        />
                        {emailValidationMsg && (
                            <div className="field-error">{emailValidationMsg}</div>
                        )}
                        {fieldErrors.Recipients && (
                            <div className="field-error">
                                {fieldErrors.Recipients}
                            </div>
                        )}
                    </Form.Field>

                    <EmailPreviewSection
                        scheduleName={form.scheduleName}
                        includeAllTemplates={form.includeAllTemplates}
                        templateIds={form.templateIds}
                        templateOrderSaved={Boolean(form.templateOrderSaved)}
                        columns={form.columns || []}
                        templateOptions={templateOptions}
                        templateRecords={templateRecords}
                        recipientCount={preview.recipients}
                    />

                    <FooterActions>
                        <AppPrimaryButton type="submit" loading={loading}>
                            {isEdit ? 'Save schedule' : 'Save schedule'}
                        </AppPrimaryButton>
                        <AppButton type="button" onClick={onCancel}>
                            Cancel
                        </AppButton>
                        {isEdit && (
                            <>
                                <AppButton
                                    type="button"
                                    onClick={onTestSend}
                                    title="Send test email now"
                                >
                                    Send test email now
                                </AppButton>
                                <AppButton
                                    type="button"
                                    negative
                                    onClick={onFireNow}
                                >
                                    🚨 Fire Now
                                </AppButton>
                                <AppButton
                                    type="button"
                                    negative
                                    basic
                                    onClick={onDelete}
                                >
                                    Delete schedule
                                </AppButton>
                            </>
                        )}
                    </FooterActions>
                </div>

                <PreviewColumn>
                    <PreviewCard>
                        <div className="preview-title">Live preview</div>
                        <div className="preview-row">
                            <span className="label">Cadence</span>
                            <span className="value">{preview.cadence}</span>
                        </div>
                        <div className="preview-row">
                            <span className="label">Days</span>
                            <span className="value">{preview.days}</span>
                        </div>
                        <div className="preview-row">
                            <span className="label">Next send</span>
                            <span className="value">{preview.nextSend}</span>
                        </div>
                        <div className="preview-row">
                            <span className="label">Recipients</span>
                            <span className="value">{preview.recipients}</span>
                        </div>
                        <div className="preview-row">
                            <span className="label">If empty</span>
                            <span className="value" style={{ color: '#6b7280' }}>
                                {preview.ifEmpty}
                            </span>
                        </div>

                        <div className="window-highlight">
                            <div
                                className="preview-title"
                                style={{ marginBottom: 8 }}
                            >
                                Window <NewBadge>NEW</NewBadge>
                            </div>
                            <div className="preview-row">
                                <span className="label">Kind</span>
                                <span className="value">
                                    {preview.window?.kindLabel}
                                </span>
                            </div>
                            {preview.window?.showWeek && (
                                <div className="preview-row">
                                    <span className="label">Week starts</span>
                                    <span className="value">
                                        {preview.window?.weekStarts}
                                    </span>
                                </div>
                            )}
                            {preview.window?.showRolling && (
                                <div className="preview-row">
                                    <span className="label">Days</span>
                                    <span className="value">
                                        Last {preview.window?.rollingDays}
                                    </span>
                                </div>
                            )}
                            <div className="preview-row">
                                <span className="label">Preview</span>
                                <span className="value">
                                    {preview.window?.preview}
                                </span>
                            </div>
                        </div>
                    </PreviewCard>
                </PreviewColumn>
            </FormGrid>

            {isEdit && (
                <RecentFiresSection>
                    <h4>Recent fires</h4>
                    <RunHistoryList
                        data={recentRuns}
                        timezone={effectiveTimezone}
                        onRowClick={onRunClick}
                        compact
                        filter={{ PageNumber: 1, PageSize: 10 }}
                        totalCount={recentRuns?.length || 0}
                        pageChanged={() => {}}
                    />
                    {runsLoading && (
                        <div style={{ color: '#6b7280', fontSize: 12 }}>
                            Loading run history...
                        </div>
                    )}
                </RecentFiresSection>
            )}
        </Form>
    )
}
